import { httpRequest, http, request } from '@/utils/request';
import { CommonResponse } from '@/types';

export function getDeviceStatusTemplate() {
  return httpRequest<any, CommonResponse<string[]>>({
    url: '/api/v1/device/status-template',
  });
}

export function getDeviceMqttCredentials(deviceId: string) {
  return httpRequest<any, CommonResponse<{
    client_id: string;
    username: string;
    password: string;
  }>>({
    url: `/api/v1/device/${deviceId}/mqtt-credentials`,
  });
}


export function getDeviceStatusRefresh(deviceId: string) {
  return httpRequest<any, CommonResponse<{ status: string }>>({
    url: `/api/v1/device/${deviceId}/status/refresh`,
  });
}

export interface IDeviceDetail {
  id: string;
  cloud_device_id: string;
  cloud_product_id: string;
  cloud_instance_id: string;
  name: string;
  status: string;
  description: string;
  product_id: string;
  product_name: string;
  secret: string;
  platform: string;
  device_service_name: string;
  last_sync_time: number;
  last_online_time: number;
  create_at: number;
  is_ipc?: boolean;
  device_sn: string;
  model: string;
  addr: string;
  device_type: string;
  child_device_count: number;
  ip: string;
  port: string;
  location: string;
  manufacturer: string;
  modified_at: number;
  slaveId: string;
  period: string;
  // 新增字段
  last_update_ts?: number; // 最后上报时间
  device_group_name?: string; // 设备分组名称
  imei?: string; // IMEI
  firmware?: string; // 固件版本
  product_key?: string; // 产品编码
  product_brand?: string; // 产品品牌
  product_node_type?: string; // 产品节点类型
  product_protocol?: string; // 接入协议
  product_description?: string; // 产品描述
  // 激活相关
  is_activated?: boolean;       // 是否已激活
  valve_locked?: boolean;       // 电磁阀是否锁止
  accumulated_flow?: number;    // 累计流量(L)
  activation_code?: string;     // 激活码
  activation_time?: number;     // 激活时间
}

export function getDeviceDetail(id: string) {
  return httpRequest<any, CommonResponse<IDeviceDetail>>({
    url: `/api/v1/device/${id}`,
  });
}

export function getDeviceDetailData({ id }: { id: string }) {
  return httpRequest<any, CommonResponse<IDeviceDetail>>({
    url: `/api/v1/device/${id}`,
  });
}

export function addDevice(data: { name: string; product_id: string; driver_instance_id: string; description?: string; image: string; lon: string; lat: string; device_sn?: string }) {
  return httpRequest<any, CommonResponse<any>>({
    url: '/api/v1/device',
    method: 'post',
    data,
  });
}

export function editDevices(data: { name: string; id: string; description: string; image: string; lon: string; lat: string }) {
  return httpRequest<any, CommonResponse<any>>({
    url: `/api/v1/device/${data.id}`,
    method: 'put',
    data,
  });
}

export function deleteDevice(id: string) {
  return httpRequest<any, CommonResponse<any>>({
    url: `/api/v1/device/${id}`,
    method: 'delete',
  });
}

export function deviceSync(data: { cloud_instance_id: string; driver_instance_id: string }) {
  return httpRequest<any, CommonResponse<any>>({
    url: '/api/v1/device-sync',
    method: 'post',
    data,
  });
}

export function deviceSyncSingle(data: { device_id: string }) {
  return httpRequest<any, CommonResponse<any>>({
    url: `/api/v1/device-sync/${data.device_id}`,
    method: 'post',
    data,
  });
}

export function uploadValidated(data: FormData) {
  return http({
    url: '/api/v1/device/upload-validated',
    method: 'post',
    data,
  });
}

export async function getDeviceMaps(params: { status?: string; name?: string }) {
  return request<CommonResponse<{ lat: string; lon: string; name: string; status: string }[]>>(`/api/v1/device/maps`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
    params,
  });
}

// ==================== 二维码激活相关接口 ====================

/** 设备激活状态 */
export interface DeviceActivationInfo {
  is_activated: boolean;       // 是否已激活
  activation_code: string;     // 激活码
  activation_time?: number;    // 激活时间
  activated_by?: string;       // 激活人
  qrcode_url: string;          // 二维码图片URL
  device_sn: string;           // 设备唯一编码
}

/** 批量生成二维码请求参数 */
export interface BatchGenerateQrcodeParams {
  product_id: string;          // 产品ID
  count: number;               // 生成数量
}

/** 批量生成二维码响应 */
export interface BatchGenerateQrcodeResult {
  list: Array<{
    device_id: string;
    device_sn: string;
    qrcode_url: string;
    activation_code: string;
  }>;
}

/** 获取设备激活信息 */
export function getDeviceActivation(deviceId: string) {
  return httpRequest<any, CommonResponse<DeviceActivationInfo>>({
    url: `/api/v1/device/${deviceId}/activation`,
  });
}

/** 生成单个设备二维码 */
export function generateDeviceQrcode(deviceId: string) {
  return httpRequest<any, CommonResponse<{ qrcode_url: string; device_sn: string; activation_code: string }>>({
    url: `/api/v1/device/${deviceId}/qrcode`,
    method: 'post',
  });
}

/** 批量生成设备二维码（按产品） */
export function batchGenerateQrcode(data: BatchGenerateQrcodeParams) {
  return httpRequest<any, CommonResponse<BatchGenerateQrcodeResult>>({
    url: '/api/v1/device/qrcode/batch',
    method: 'post',
    data,
  });
}

/** 扫码激活设备（小程序/客户端调用） */
export function activateDeviceByCode(activationCode: string, providerId?: string) {
  const data: any = { activation_code: activationCode };
  if (providerId) data.provider_id = providerId;
  return httpRequest<any, CommonResponse<{
    device_id: string;
    device_name: string;
    product_name: string;
    is_activated: boolean;
  }>>({
    url: '/api/v1/device/activate-by-code',
    method: 'post',
    data,
  });
}

/** 锁止/解锁设备电磁阀 */
export function lockDeviceValve(deviceId: string, locked: boolean, reason?: string) {
  return httpRequest<any, CommonResponse<{ success: boolean }>>({
    url: `/api/v1/device/${deviceId}/valve`,
    method: 'post',
    data: { locked, reason },
  });
}
